import { TitleCasePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { TabDirective } from "ngx-bootstrap/tabs";
import { BehaviorSubject, Observable, Subject, Subscription, asyncScheduler, firstValueFrom } from "rxjs";
import { debounceTime, first } from "rxjs/operators";
import { AppState, DbEntityChangedEvent, RoleMgr, ShowRequest } from "../../core";
import { AppTab } from "../../core/app-tab";
import { CorrelationRecord, MiscLeafNode, PathWaypoint, Point, ScoutPath, TaskStatus, activity_status, isAndroid, isIos } from "../../core/models";
import { ScoutTaskDoc } from "../../core/schema/scout-task-schema";
import { PathWaypointService, PointService, PostProcessingService } from "../../core/services";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { TableViewContext, TableViewQueryCriteria } from "../../shared/components/table-view/table-view-component-base";
import { FesRutTaskConfig } from "../models";
import { VolunteerTaskMapPoint } from "../models/volunteer-task-map-point";
import { VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import { VolunteerTaskViewRecord } from "../models/volunteer-task-record";
import { ScoutPointInfo } from "../../core/services/scout-point.service";
import { VolunteerTaskViewService } from "../services/volunteer-task-view.service";
import { VolunteerTaskService } from "../services/volunteer-task.service";
import { VolunteerService } from "../services/volunteer.service";
import { VolunteerEditComponent } from "../volunteer-edit/volunteer-edit.component";
import { VolunteerTaskEditComponent } from "../volunteer-task-edit/volunteer-task-edit.component";
import { VolunteerTaskPointComponent } from "../volunteer-task-point/volunteer-task-point.component";
import { TaskPtRecs } from "../volunteer-task-points/volunteer-task-points-table.component";
import { VolunteerTaskMapComponent } from "../volunteer-task-map/volunteer-task-map.component";
import { CorrelationTableComponent } from "../../post-processing/correlation-table/correlation-table.component";
import { debounce } from "lodash";

export type SelectedTaskPointContext = {
  taskPoint: VolunteerTaskPointViewRecord;
  scoutInfo: ScoutPointInfo;
  mapPoint: VolunteerTaskMapPoint;
};

class TmpVolunteerTaskMapPoint extends VolunteerTaskMapPoint {
  constructor(
    pathWaypoint: PathWaypoint,
    point: Point,
    volunteerPoint: VolunteerTaskPointViewRecord
  ) {
    super(pathWaypoint, point, volunteerPoint);
  }

  get exPointId() {
    return this.pointId;
  }
}
@Component({
  selector: "app-volunteer-task",
  templateUrl: "volunteer-task.component.html",
  styleUrls: ["volunteer-task.component.scss"],
  providers: [TitleCasePipe],
})
export class VolunteerTaskComponent implements OnInit, OnDestroy {
  @Input() task: VolunteerTaskViewRecord;

  @Input("taskId") set taskIdSetter(taskId: number | string) {
    console.log(`taskIdSetter(${taskId})`)
    if (typeof taskId === "number") {
      this.taskId = taskId;
      return;
    }
    try {
      const context = JSON.parse(taskId);
      if (context.taskId) {
        this.taskId = parseInt(context.taskId);
        if ( context.taskPointId != this.selectedTaskPoint?.id  ) {
          this.initialEditTaskPointId = context.taskPointId;
        }
      } else {
        this.taskId = parseInt(taskId);
      }
    } catch (error: any) {
      console.error(error);
    }
  }
  taskId: number;

  @Input("tabConfig") set tabConfigSetter(tabConf: AppTab) {
    this.tabConfig = tabConf;
    this.initTabRemovalBehavior(tabConf);
    tabConf.setSelected = this.onTabSelected;
    this.closeTab = tabConf.close;
  }

  @Input() detailFn: (vi: ShowRequest) => void;

  @Input() isActiveView: boolean;

  @ViewChild('taskPointComponent')  taskPointComponent: VolunteerTaskPointComponent;
  @ViewChild("taskMap") taskMap: VolunteerTaskMapComponent;
  @ViewChild("correlationTable") correlationTable: CorrelationTableComponent;

  @Output("contextJsonChange") contextChangeEmitter = new EventEmitter<string>();

  @ViewChild("leftDiv") set leftDiv(value: ElementRef<HTMLDivElement>) {
    this.resizeLeftDivSub?.unsubscribe();

    if (value) {
      const resizeObservable = new Observable<ResizeObserverEntry[]>(subscriber => {
        const observer = new ResizeObserver(entries => {
          subscriber.next(entries);
        });
        observer.observe(value.nativeElement);
        return () => observer.disconnect();
      });
      this.resizeLeftDivSub = resizeObservable.pipe(
        debounceTime(1000)
      ).subscribe(entries => {
        this.ngZone.run(()=>{
          this.taskMap.refresh();
        });
      });
    }
  };
  private resizeLeftDivSub: Subscription;


  closeTab: () => void;
  tabConfig: AppTab;
  bTabActive = false;
  statuses = TaskStatus.Ids;
  errorMsg = "";
  localContext = "";

  bMapResetting = false;
  loading = false;
  mapPoints: VolunteerTaskMapPoint[] = [];
  selectedTaskPoint: VolunteerTaskPointViewRecord;

  processingPoints = false;
  taskPoints: VolunteerTaskPointViewRecord[] = [];
  scoutSources: ScoutPointInfo[] = [];
  initialEditTaskPointId: number;

  pointTabMode = false;
  selectedPointState$ = new BehaviorSubject<SelectedTaskPointContext>(
    null
  );

  isNotesSaveButtonShown: boolean = false; // hidden by default

  notesFieldDisabled: boolean = true;

  notes: string = null;

  notesFieldDirty: boolean = false;

  extractedPathIds: string[] = [];
  scoutInfoByRecId: { [recId: string]: ScoutPointInfo };

  taskConfig: FesRutTaskConfig;
  correlationContext: string;

  scoutAndPathResolver$ = new EventEmitter<{
    pathId: string;
    scoutTask: ScoutTaskDoc;
    scoutPath: ScoutPath;
  }>();

  constructor(
    protected appState: AppState,
    public roleMgr: RoleMgr,
    protected volunteerService: VolunteerService,
    protected volunteerTaskViewService: VolunteerTaskViewService,
    protected volunteerTaskService: VolunteerTaskService,
    protected bsModalService: BsModalService,
    protected titleCasePipe: TitleCasePipe,
    protected waypointService: PathWaypointService,
    protected pointService: PointService,
    protected postProcessingService: PostProcessingService,
    protected ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.init().catch(e=>this.volunteerTaskService.publishError(e,this));
  }

  init = async () => {
    console.log(`init() for taskId ${this.taskId}`);
    try {
      this.loading = true;
      this.taskConfig = await this.volunteerTaskService.getRemoteConfig();
      this.errorMsg = "";
      this.localContext = "";
      this.bDeleted = false;
      this.task = await this.volunteerTaskViewService.getById(this.taskId);
      this.notes = this.task.notes;
      this.correlationContext = JSON.stringify(this.buildCorrelationContext());
      console.log(`init()`);
      console.log('task', this.task);
      if (this.task) {
        //console.log(`init() have task for ${this.task.buildingId}`);
        this.tabConfig.viewInfo.title.next(
          `Mobile ${this.titleCasePipe.transform(this.task.type)} ${
            this.task.buildingId
          }`
        );
      }
      const context: TableViewQueryCriteria = {
        filters: [{ key: "taskId", value: `${this.task.id}`, mode: "=" }],
      };
      this.localContext = JSON.stringify(context);
      this.initDbEntityChangeSubscription();
    } catch (error: any) {
      this.errorMsg = `Failed loading task for id '${this.taskId}'. ${
        error?.message || error
      }`;
    } finally {
      this.loading = false;
    }
  };

  dbEntityChangeSub?: Subscription;
  reloadSub?: Subscription;
  private reloadSubject = new Subject<void>();

  initDbEntityChangeSubscription = () => {
    this.dbEntityChangeSub?.unsubscribe();
    this.reloadSub?.unsubscribe();
    this.reloadSub = this.reloadSubject.pipe(
      debounceTime(1_000)
    ).subscribe({
      next: () => {
        this.init().catch(e=>this.volunteerTaskService.publishError(e,this,5))
        this.pointsTableLoadTrigger.next();
      },
      error: (e) => this.volunteerTaskService.publishError(e, this)
    });
    this.dbEntityChangeSub = this.appState.dbEntityChange$.subscribe({
      next: this.onEntityChanged
    });
  }

  onEntityChanged = (changeEvent: DbEntityChangedEvent) => {
    console.log(`VolunteerTasksTableComponent.onEntityChanged: ${JSON.stringify(changeEvent)}`);
    if ( 'volunteer_task_change' === changeEvent.channel ) {
      console.log(`VolunteerTasksTableComponent.onEntityChanged: volunteer_task_change`,changeEvent.payload.id === this.task.id);
      if ( changeEvent.payload.id === this.task.id ) {
        this.reloadSubject.next();
      }
    } else if ( 'volunteer_task_point_change' === changeEvent.channel ) {
      const taskPointChange: any = changeEvent.payload;
      console.log(`VolunteerTasksTableComponent.onEntityChanged: volunteer_task_point_change`,taskPointChange.taskId === this.task.id);
      if ( taskPointChange.taskId === this.task.id ) {
        this.reloadSubject.next();
      }
    } else if ( 'volunteer_point_change' === changeEvent.channel ) {
      const taskPoints = this.taskPoints.filter(
        (tp: VolunteerTaskPointViewRecord) => tp.volunteerPointId === changeEvent.payload.id
      );
      console.log(`VolunteerTasksTableComponent.onEntityChanged: volunteer_point_change`,taskPoints.length);
      if ( taskPoints.length > 0 ) {
        this.reloadSubject.next();
      }
    }
  }


  ngOnDestroy(): void {
    this.dbEntityChangeSub?.unsubscribe();
    this.reloadSub?.unsubscribe();
    this.resizeLeftDivSub?.unsubscribe();
    this.unsubscribe();
  }

  togglePointTab() {
    this.pointTabMode = !this.pointTabMode;
    if ( !this.pointTabMode ) {
      this.contextChangeEmitter.next(`${this.task.id}`);
    }
  }

  toggleCorrelationVisible = () => {
    this.correlationVisible =!this.correlationVisible;
    if ( !this.correlationVisible) {
      this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => mpt.miscChildren = []);
      this.taskMap?.plotWhenReady(this.mapPoints);
    } else {
      // template will add table and trigger load
    }
  }

  isEditing = () => this.hasUnsavedNotes();

  unsubscribe() {}

  getStatusName(statusName: any) {
    return TaskStatus.getStatusName(statusName);
  }

  taskPointsContextChanged(event: any) {
    console.dir(event);
  }

  initTabRemovalBehavior(tabConfig: any) {
    tabConfig.isEditing = this.isEditing;
    const defaultRemove = tabConfig.remove;
    tabConfig.remove = (te: TabDirective) => {
      if (!this.isEditing()) {
        defaultRemove(te);
      } else {
        if (!this.notesFieldDisabled) {
          this.askDiscardEdits(() => defaultRemove(te));
        } else if (!this.notesFieldDisabled) {
          this.askDiscardEdits(() => defaultRemove(te));
        }
      }
    };
  }

  onShowBuilding() {
    const buildingId = this.task?.buildingId;
    if (buildingId) {
      this.appState.showView.next(
        new ShowRequest("tab", "building", buildingId, null)
      );
    }
  }

  async onShowVolunteer() {
    try {
      if (!this.task) {
        throw new Error("Volunteer task is null.  Cannot show volunteer.");
      }
      const volunteer = await this.volunteerService.getByIdEagerly(this.task.volunteerId);
      if (volunteer) {
        const modal = VolunteerEditComponent.showModal(this.bsModalService,volunteer);
        modal.onHidden.subscribe((data) => {
          this.init();
        });
      } else {
        throw new Error(
          `Could not find volunteer (id=${this.task.volunteerId}, name=${this.task.volunteerName})`
        );
      }
    } catch (error) {
      console.error(error);
      this.appState.notifyWarning(
        this.volunteerService.extractErrorMsg(error),
        this,
        5
      );
    }
  }

  onTabSelected = (bSelected: boolean) => {
    //console.log("volunteer task tab selected: " + bSelected);
    this.bTabActive = bSelected; // prepare to show path plotter
    if (bSelected) {
      this.init();
    } else {
      this.unsubscribe();
    }
    //console.dir(this.tabConfig);
  };

  async onShowEditModal() {
    let task = this.task;
    //console.log(`onShowEditModal(${task?.id} (bldg:${task?.buildingId}))`);
    const initialState = {};
    try {
      const modalRef = this.bsModalService.show(VolunteerTaskEditComponent, {
        ignoreBackdropClick: true,
        class: "modal-xl",
        initialState,
      });
      const taskEditComponent: VolunteerTaskEditComponent = modalRef.content;
      if (!task) {
        task = new VolunteerTaskViewRecord();
      }
      taskEditComponent.setTask(task);
      taskEditComponent.doAfterSaveFn = async () => {
        if (!task?.id) {
          this.pointsTableLoadTrigger.next();
        } else {
          await this.init();
          this.pointsTableLoadTrigger.next();
        }
      };
      taskEditComponent.doAfterDeleteFn = (deletedRec) => {
        this.bDeleted = true;
      };

      await firstValueFrom(modalRef.onHidden);
    } catch (error) {
      console.error(error);
      this.volunteerTaskService.publishError(error, this);
    }
  }

  pointsTableLoadTrigger = new EventEmitter<void>();
  bDeleted = false;

  async onDeleteTask() {
    const deletedTask = await this.volunteerTaskViewService.promptedDeleteTask(
      this.task
    );
    this.bDeleted = !!deletedTask;
  }

  async onDeleteSelectedTaskPoint() {
    await this.taskPointComponent?.onDeleteTaskPoint();
    this.pointsTableLoadTrigger.next();
  }

  onMapPointSelected(mwp: VolunteerTaskMapPoint) {
    //console.log("onMapPointSelected() " + mwp?.id);
    const rec = mwp?.taskPoint;
    if (rec) {
      const scoutInfo = this.scoutInfoByRecId?.[rec?.id];
      this.selectPoint({ rec, scoutInfo });
    } else {
      this.selectedPointState$.next({
        taskPoint: null,
        mapPoint: mwp,
        scoutInfo: null,
      });
    }
  }

  onTaskPointModified(vtp: VolunteerTaskPointViewRecord) {
    const pt = this.taskPoints.find(pt=>pt.id==vtp.id);
    if ( pt ) {
      Object.keys(pt).forEach(key => delete pt[key]);
      Object.assign(pt,vtp);
    }
  }

  onTaskPointSelected(vpt: VolunteerTaskPointViewRecord) {
    //console.log(`onTaskPointSelected(${vpt?.pointId})`);
    const mapPoint = this.mapPoints.find(
      (mpt) => mpt.taskPoint?.id == vpt.id
    );
    this.onMapPointSelected(mapPoint);
  }

  onRelativeTaskPointSelected(relativeOffset: number) {
    //console.log(`onRelativeTaskPointSelected(${relativeOffset})`);
    let selectedIndex = this.taskPoints.findIndex(
      (vpt) => vpt.id === this.selectedTaskPoint?.id
    );
    this.onTaskPointSelected(
      this.taskPoints[selectedIndex + relativeOffset] ||
        this.taskPoints[0]
    );
  }

  onTaskPointEditClick(taskPointInfo: {
    rec: VolunteerTaskPointViewRecord;
    scoutInfo: ScoutPointInfo;
  }) {
    this.selectPoint(taskPointInfo);
    this.pointTabMode = true;
  }

  onTaskPointIdClick(taskPointInfo: {
    rec: VolunteerTaskPointViewRecord;
    scoutInfo: ScoutPointInfo;
  }) {
    this.selectPoint(taskPointInfo);
  }

  protected selectPoint(taskPointInfo: {
    rec: VolunteerTaskPointViewRecord;
    scoutInfo: ScoutPointInfo;
  }) {
    // console.log(
    //   `selectPoint(rec:${JSON.stringify(
    //     taskPointInfo.rec
    //   )},hasScoutInfo:${!!taskPointInfo.scoutInfo})`
    // );
    this.selectedTaskPoint = taskPointInfo?.rec;
    const scoutInfo =
    taskPointInfo?.scoutInfo ||
      this.scoutInfoByRecId?.[this.selectedTaskPoint?.id];
    const mapPoint = this.mapPoints.find(
      (mp) =>
        mp.taskPoint &&
        mp.taskPoint.id === this.selectedTaskPoint?.id
    );
    this.selectedPointState$.next({
      taskPoint: this.selectedTaskPoint,
      scoutInfo,
      mapPoint,
    });
    if ( this.pointTabMode) {
      asyncScheduler.schedule(()=>{
        const idObj = {
          taskId: this.task.id,
          taskPointId: this.selectedTaskPoint.id
        }
        this.contextChangeEmitter.next(JSON.stringify(idObj));
      })
    }
  }

  async onTaskPointsChanged(taskPtRecs: TaskPtRecs) {
    //console.log(`onTaskPointsChanged()`);
    const taskPoints = taskPtRecs.recs;
    try {
      this.processingPoints = true;
      const extractedPathIds = [
        ...new Set(
          taskPoints.map((tp) => tp.pathId).filter(pathId => !!pathId)
        ),
      ];
      // grab waypoints for all paths (map doesn't care which path a point came from)
      let waypoints: PathWaypoint[];
      let aggPathPoints: Point[];
      try {
        //waypoints = (await this.waypointService.findByAttrIn('pathId', extractedPathIds)).map(doc=>doc.toJSON());
        waypoints = await this.waypointService.restClient.findByAttrIn('pathId',extractedPathIds);
      } catch ( e: any ) {
        const msg = `Issue finding waypoints using path ID for each task point (path: ${extractedPathIds})`;
        console.error(msg,'Failed getting waypoints for pathIds', extractedPathIds);
        throw new Error(msg);
      }
      try {
        aggPathPoints = await this.pointService.restClient.getByIds(
          waypoints.map((pwp) => pwp.pointId)
        );
      } catch ( e: any ) {
        const msg = `Issue finding points using path ID for each task point (path: ${extractedPathIds})`;
        console.error(msg,'Failed getting points from rxCollection');
        throw new Error(msg);
      }
      let mapPoints: VolunteerTaskMapPoint[];
      try {
        mapPoints = waypoints.map(
          (pwp) =>
            new VolunteerTaskMapPoint(
              pwp,
              aggPathPoints.find((pt) => pt._id === pwp.pointId),
              taskPoints.find(
                (vp) => vp.pathId === pwp.pathId && vp.pointId === pwp.pointId
              )
            )
        );
      } catch ( e: any ) {
        console.error(e);
        throw new Error(`Issue adding task points to map plotting data (paths: ${taskPoints?.map(tp=>tp?.pathId)})`);
      }
      try {
        const pathlessVolPoints = taskPoints.filter((pt) => !pt.pathId);
        let tmpPathWayPointCnt = 0;
        const genNextTempPathWaypointId = () =>
          `TMP_WAYPOINT_${++tmpPathWayPointCnt}`;
        for (const vpt of pathlessVolPoints) {
          //const point = await this.pointService.getById(vpt.pointId);
          const point = await this.pointService.restClient.getById(vpt.pointId);
          const pwp: PathWaypoint = {
            pointId: vpt.pointId,
            fromPointId: null,
            waypointOrder: -1,
            _id: genNextTempPathWaypointId(),
            segment: "",
            pathId: null,
            type: point?.type,
          };
          const mapPt = new TmpVolunteerTaskMapPoint(pwp, point, vpt);
          if (point?.latitude === undefined || point?.longitude === undefined) {
            console.warn("Pathless point is missing lat/lng data.", mapPt);
          }
          mapPoints.push(mapPt);
        }
      } catch ( e: any ) {
        console.error(e,taskPoints);
        this.waypointService.publishErrorObj(e,
          `Issue finding/adding pathless points to map plotting data (path: ${extractedPathIds})`,
          this, 5*60
        );
      }
      this.waypointService.updatePathContext(mapPoints);
      this.mapPoints = mapPoints;
      if ( this.correlationVisible ) {
        await this.correlationTable?.load();
      }
    } catch (error: any) {
      console.error(error,taskPoints);
      this.waypointService.publishError(error, this,5*60);
    } finally {
      this.processingPoints = false;
    }
    this.taskPoints = taskPoints;
    this.scoutInfoByRecId = taskPtRecs.scoutInfoByRecId;
    this.updateDisabledFromScoutInfo(this.mapPoints);
    const scoutInfoArr = Object.values(this.scoutInfoByRecId);
    this.scoutSources = scoutInfoArr.reduce((acc, scoutInfo) => {
      if (
        scoutInfo.scoutPath &&
        !acc.some(
          (si) =>
            scoutInfo.pathId === si.pathId &&
            scoutInfo.scoutPath?.id === si.scoutPath?.id
        )
      ) {
        acc.push(scoutInfo);
      }
      return acc;
    }, []);

    if (this.initialEditTaskPointId) {
      const volPointId = this.initialEditTaskPointId;
      this.initialEditTaskPointId = null;
      const taskPoint = taskPoints?.find(
        (taskPt) => taskPt.id === volPointId
      );
      if (taskPoint) {
        asyncScheduler.schedule(() =>
          this.onTaskPointEditClick({
            rec: taskPoint,
            scoutInfo: taskPtRecs.scoutInfoByRecId[taskPoint?.id],
          })
        );
      }
    } else if (
      !this.selectedPointState$.value?.taskPoint &&
      taskPoints?.length
    ) {
      asyncScheduler.schedule(() =>
        this.selectPoint({
          rec: taskPoints[0],
          scoutInfo: taskPtRecs.scoutInfoByRecId[taskPoints[0]?.id],
        })
      );
    }
  }

  updateDisabledFromScoutInfo(mapPts: VolunteerTaskMapPoint[]) {
    for( const mapPt of mapPts) {
      const scoutPtInfo = mapPt.taskPoint?.id && this.scoutInfoByRecId?.[mapPt.taskPoint.id];
      mapPt.disabled = scoutPtInfo?.scoutPoint?.disabled||false;
    }
  }

  //TBD - remove this method?
  onScoutAndPathResolved(
    pathId: string,
    scoutAndPath: { scoutTask: ScoutTaskDoc; scoutPath: ScoutPath }
  ) {
    //console.log("onScoutAndPathResolved()", pathId);
    //console.dir(scoutAndPath);
    this.scoutAndPathResolver$.next({
      pathId,
      scoutTask: scoutAndPath?.scoutTask,
      scoutPath: scoutAndPath?.scoutPath,
    });
  }

  getVolunteerId() {
    return "VOLUNTEER_ID_" + this.task.id;
  }

  // Editable notes functionality for admin
  hasUnsavedNotes() {
    if (this.notesFieldDisabled == false) {
      return this.notes?.length;
    }
  }

  askDiscardEdits(acceptDiscardFn: () => void) {
    let initialState = {
      onYes: acceptDiscardFn,
      question: "Discard notes field changes?",
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    this.bsModalService.show(ConfirmModalComponent, { initialState });
  }

  notesChangeFn(e) {
    let countDirtyValues = 0;
    Object.values(e).map((value) => {
      if (value) {
        countDirtyValues += 1;
      }
    });
    if (countDirtyValues >= 1) {
      this.notesFieldDirty = true;
    }
    this.notes = e;
  }

  async onSaveNotes() {
    this.task.notes = this.notes;
    await this.volunteerTaskViewService.saveWithoutPoints(this.task);
    this.notesFieldDirty = false;
    this.isNotesSaveButtonShown = false;
    this.notesFieldDisabled = !this.notesFieldDisabled;
  }

  async onCancel() {
    if (this.notesFieldDirty == false) {
      this.notesFieldDisabled = true;
      this.isNotesSaveButtonShown = false;
      return;
    }
    let modalResponseAffirm: boolean = false;
    const initialState = {
      onYes: () => {
        modalResponseAffirm = true;
        this.notesFieldDirty = false;
      },
      onNo: () => {
        modalResponseAffirm = false;
      },
      question: "Discard notes field changes?",
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    let data = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      initialState,
    });
    await data.onHidden
      .pipe(first())
      .toPromise()
      .then((data) => {
        if (modalResponseAffirm) {
          this.notesFieldDisabled = true;
          this.isNotesSaveButtonShown = false;
          this.notes = this.task.notes;
        } else {
          this.isNotesSaveButtonShown = true;
          this.notesFieldDisabled = false;
        }
      });
  }

  editNotes() {
    if (this.roleMgr.canEditTasks) {
      this.notesFieldDisabled = !this.notesFieldDisabled;
      this.isNotesSaveButtonShown = true;
    }
  }

  correlationVisible = false;

  onCorrelationVisibleChange(visible: boolean) {
    if ( !visible ) {
      this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => {
        mpt.miscChildren = [];
      });
    }
    this.taskMap.plotWhenReady(this.mapPoints);
  }

  onCorrelationRecordsLoaded = (rows: CorrelationRecord[]) => {
    this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => {
      const ptCorrelationRecs = rows.filter(cr=>cr.pointid == mpt.pointId);
      mpt.miscChildren = [];
      const children: MiscLeafNode[] = mpt.miscChildren;
      ptCorrelationRecs.forEach(cr=>{
        if ( cr.latitude != null && cr.longitude != null ) {
          children.push( {
            latitude: Number(cr.latitude),
            longitude: Number(cr.longitude),
            id: cr.viewrowid,
            parentId: mpt.id,
            title: `${cr.pointid} | ${cr.horizontalerror?.toFixed(2)}m | ${cr.carrier} | ${cr.type} | ${cr.summarytech}`,
            iconClass: isAndroid(cr.os) ? 'fab fa-android android-icon' : isIos(cr.os) ? 'fab fa-apple apple-icon' : undefined,
            type: 'call log',
            data: cr
          })
        }
      });
    });
    this.taskMap.plotWhenReady(this.mapPoints);
  }

  onMiscLeafNodeEvent = (evt: {srcEvent: MouseEvent, id : number, parentId: string}) => {
    //console.log('onMiscLeafNodeEvent()', evt, evt.srcEvent.type);
    const recIndex = this.correlationTable.records.findIndex(cr=>cr.viewrowid == evt.id);
    if ( recIndex === -1 ) {
      console.warn('onMiscLeafNodeEvent() no correlation record found for id', evt.id);
      return;
    }
    const rec = this.correlationTable.records[recIndex]
    if ( evt.srcEvent.type==='click' ) {
      this.correlationTable.showDetailModal(rec);
    } else if ( evt.srcEvent.type==='mouseover') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[recIndex],
        clearPreviousSelection:true,
        scrollIntoViewMode: false //TODO - only support scroll into view from a popup menu item
      });
    } else if (evt.srcEvent.type==='mouseout') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[],
        clearPreviousSelection:true,
      });
    }
  }

  correlationRowHover = (hoverEvt: { event:MouseEvent,record: CorrelationRecord}, active: boolean) => {
    //console.log('correlationRowHover()', hoverEvt,active);
    this.taskMap?.highlightLeafNode(hoverEvt.record.viewrowid, active);
  }

  buildCorrelationContext(): Partial<TableViewContext> {
    return {
      filters: [
        {
          key: 'stage',
          value: this.task.stage,
          mode: '='
        },
        {
           key: 'cycle',
           value: `${this.task.seriesId}-${this.task.seriesPosition}`,
           mode: '='
        },
        {
           key: 'completedcall',
           value: 'true',
           mode: '='
        }
      ],
      visibleNames:[
        'pointid', 'os',
         'callstarttime','callduration','rantech','summarytech','type',
         'horizontalerror','verticalerror', 'locationtime','timetofix'
      ],
      sorts: [
        {
          key: 'horizontalerror',
          order: 'ASC'
        },
        {
          key: 'locationtime',
          order: 'DESC'
        }
      ]
    }
  }

}
